import type { SidebarNavItem } from "@pericles/framework-frontend";

import type { FoundryV2NavigationSnapshotRecord } from "../api/foundryV2Client";

export type FoundryV2RolloutAwareTarget = "foundry-v2" | "jarvis-v2" | "governance-v2";

function readHandoffProjectId(storagePayload: string | null | undefined): string {
  if (!storagePayload) return "";
  try {
    const parsed = JSON.parse(storagePayload) as { projectId?: string };
    return typeof parsed.projectId === "string" ? parsed.projectId.trim() : "";
  } catch {
    return "";
  }
}

export function isFoundryV2RolloutAwarePath(pathname: string): boolean {
  return (
    pathname === "/jarvis" ||
    pathname === "/jarvis-v2" ||
    pathname === "/foundry-v2" ||
    pathname === "/governance" ||
    pathname === "/governance-v2" ||
    pathname.startsWith("/projects/")
  );
}

export function resolveFoundryV2RolloutAwareTarget(pathname: string): FoundryV2RolloutAwareTarget | null {
  if (pathname === "/foundry-v2") return "foundry-v2";
  if (pathname === "/jarvis-v2") return "jarvis-v2";
  if (pathname === "/governance-v2") return "governance-v2";
  return null;
}

export function resolveFoundryV2ProjectId(args: {
  pathname: string;
  search: string;
  handoffStoragePayload?: string | null;
}): string {
  const params = new URLSearchParams(args.search);
  const fromQuery = params.get("projectId")?.trim();
  if (fromQuery) return fromQuery;

  const projectMatch = args.pathname.match(/^\/projects\/([^/?#]+)/);
  if (projectMatch?.[1]) {
    return decodeURIComponent(projectMatch[1]).trim();
  }

  return readHandoffProjectId(args.handoffStoragePayload);
}

function rewriteJarvisV2Label(
  item: SidebarNavItem,
  snapshot: FoundryV2NavigationSnapshotRecord,
): SidebarNavItem {
  return {
    ...item,
    label: snapshot.moduleEntry.label,
  };
}

export function applyFoundryV2NavigationSnapshotToSidebar(
  items: SidebarNavItem[],
  snapshot: FoundryV2NavigationSnapshotRecord | null,
): SidebarNavItem[] {
  if (!snapshot) return items;
  if (!snapshot.moduleEntry.visible) {
    return items.filter((item) => item.id !== snapshot.moduleEntry.id);
  }

  return items.map((item) => {
    if (item.id !== snapshot.moduleEntry.id) {
      return item;
    }
    return rewriteJarvisV2Label(item, snapshot);
  });
}

export function resolveFoundryV2SidebarHref(
  href: string,
  snapshot: FoundryV2NavigationSnapshotRecord | null,
): string {
  if (href === "/jarvis-v2" && snapshot) {
    return snapshot.moduleEntry.preferredHref;
  }
  return href;
}

export function resolveFoundryV2RouteRedirect(
  pathname: string,
  snapshot: FoundryV2NavigationSnapshotRecord | null,
): string | null {
  if (!snapshot) return null;

  const target = resolveFoundryV2RolloutAwareTarget(pathname);
  if (!target) return null;

  const resolution =
    target === "foundry-v2"
      ? snapshot.targets.foundryV2
      : target === "jarvis-v2"
        ? snapshot.targets.jarvisV2
        : snapshot.targets.governanceV2;

  return resolution.recommendation.status === "redirect" ? resolution.recommendation.route : null;
}
